/**
 * Scrapper Service
 * @description API methods for Scrapper-related operations
 */

import { scrapperApi, ApiError } from './api';
import type { URLConfig, ErrorTableEntry, ErrorTableParams, ElementCheckEntry, ElementChecksParams, StatsSummaryResponse, PerformanceSummaryResponse, StatsParams, ScrapperConfigurePayload, ScrapperConfigureResponse, ScrapperUpdateParams, ConfigurationDetailResponse, LatestVitalsResponse, MetricsByDomainResponse, VitalsGraphResponse, DeleteConfigurationResponse, SitesListParams, SitesListResponse, SiteListItem, ConfigurationWithVitalsResponse, ConfigurationWithVitalsParams, FunctionalChecksResponse, FunctionalChecksParams, ErrorsResponse, ErrorsParams, DeviceType, WinnersResponse, TrendingResponse, LosingResponse, ElementTemplatesResponse, StandardThresholdsResponse } from '@/types/urlConfig';

export type CreateScrapperConfigInput = Omit<URLConfig, 'id' | 'createdAt' | 'lastRun' | 'successRate' | 'totalRuns' | 'status'>;

export interface ScrapperListResponse {
  total: number;
  configs: URLConfig[];
}

export interface MetricsTimeseriesParams extends Record<string, unknown> {
  startDate: string; // ISO 8601 format
  endDate: string;   // ISO 8601 format
}

export interface MetricsDistributionParams extends Record<string, unknown> {
  startDate: string; // ISO 8601 format
  endDate: string;   // ISO 8601 format
}

export interface ChartResponse {
  data: Array<{
    x?: (string | number | Date)[];
    y?: (string | number | Date)[];
    name?: string;
    type?: string;
    mode?: string;
    line?: {
      width?: number;
      shape?: string;
      color?: string;
    };
    marker?: {
      size?: number;
      symbol?: string;
      color?: string | string[];
    };
    [key: string]: unknown;
  }>;
  layout: {
    title?: {
      text?: string;
      font?: {
        size?: number;
        color?: string;
      };
    };
    xaxis?: {
      title?: {
        text?: string;
        font?: { color?: string };
      };
      gridcolor?: string;
      linecolor?: string;
      tickfont?: { color?: string };
    };
    yaxis?: {
      title?: {
        text?: string;
        font?: { color?: string };
      };
      gridcolor?: string;
      linecolor?: string;
      tickfont?: { color?: string };
    };
    paper_bgcolor?: string;
    plot_bgcolor?: string;
    showlegend?: boolean;
    legend?: {
      font?: { color?: string };
      bgcolor?: string;
      bordercolor?: string;
    };
    margin?: {
      t?: number;
      r?: number;
      b?: number;
      l?: number;
    };
    template?: string;
    height?: number;
    hovermode?: string;
    hoverlabel?: {
      bgcolor?: string;
      font?: { color?: string };
    };
    [key: string]: unknown;
  };
}


export const scrapperService = {
  /**
   * Fetches all scraper configurations from the API
   * @returns Array of URLConfig objects
   * @throws ApiError if the request fails
   */
  getConfigurations: async (): Promise<SitesListResponse[]> => {
    try {
      const apiConfigs = await scrapperApi.get<SitesListResponse[]>('/scraper/configurations');
      return apiConfigs;
    } catch (error) {
      throw error instanceof ApiError ? error : new ApiError('Failed to fetch scraper configurations');
    }
  },

  /**
   * Fetches sites list with backend pagination and search
   * @param params - Pagination and search parameters
   * @returns Sites list response with data array and pagination metadata
   * @throws ApiError if the request fails
   */
  getSites: async (params?: SitesListParams): Promise<SitesListResponse> => {
    try {
      const response = await scrapperApi.get<SitesListResponse>('/scraper/sites', params as Record<string, unknown>);
      return response;
    } catch (error) {
      throw error instanceof ApiError ? error : new ApiError('Failed to fetch sites list');
    }
  },

  /**
   * Creates a new scraper configuration
   * @param payload - Scrapper configuration payload
   * @returns Configuration response with message and identifiers
   * @throws ApiError if the request fails (400 for validation, 409 for duplicates)
   */
  configureScrapper: async (payload: ScrapperConfigurePayload): Promise<ScrapperConfigureResponse> => {
    try {
      return await scrapperApi.post<ScrapperConfigureResponse>('/scraper/configure', payload);
    } catch (error) {
      throw error instanceof ApiError ? error : new ApiError('Failed to configure scraper');
    }
  },

  /**
   * Updates an existing scraper configuration
   * @param configId - The configuration ID to update
   * @param payload - Scrapper configuration payload (same as create)
   * @returns Configuration response with message and identifiers
   * @throws ApiError if the request fails (400 for validation, 404 if not found)
   */
  updateScrapperConfig: async (configId: string, payload: ScrapperConfigurePayload): Promise<ScrapperConfigureResponse> => {
    try {
      return await scrapperApi.put<ScrapperConfigureResponse>(
        '/scraper/configure',
        payload,
        { config_id: configId }
      );
    } catch (error) {
      throw error instanceof ApiError ? error : new ApiError('Failed to update scraper configuration');
    }
  },

  /**
   * Fetches detailed configuration for a specific config with optional filters
   * @param params - Configuration ID and optional filter parameters
   * @returns Configuration with vitals data, statistics, and filters applied
   * @throws ApiError if the request fails
   */
  getConfigurationDetail: async (params: ConfigurationWithVitalsParams): Promise<ConfigurationWithVitalsResponse> => {
    try {
      return await scrapperApi.get<ConfigurationWithVitalsResponse>('/scraper/configuration', params as Record<string, unknown>);
    } catch (error) {
      throw error instanceof ApiError ? error : new ApiError('Failed to fetch configuration details');
    }
  },

  /**
   * Fetches latest vitals metrics for a specific config
   * @param configId - The configuration ID
   * @returns Latest vitals including FCP, LCP, TTFB, CLS, TBT, SI
   * @throws ApiError if the request fails
   */
  getLatestVitals: async (configId: string): Promise<LatestVitalsResponse> => {
    try {
      return await scrapperApi.get<LatestVitalsResponse>('/metrics/latest-vitals', { config_id: configId });
    } catch (error) {
      throw error instanceof ApiError ? error : new ApiError('Failed to fetch latest vitals');
    }
  },

  /**
   * Fetches metrics grouped by domain
   * @param domain - The domain (protocol + hostname, e.g., "https://example.com")
   * @returns Metrics for all URLs under the domain
   * @throws ApiError if the request fails
   */
  getMetricsByDomain: async (domain: string): Promise<MetricsByDomainResponse> => {
    try {
      return await scrapperApi.get<MetricsByDomainResponse>('/metrics/by-domain', { domain });
    } catch (error) {
      throw error instanceof ApiError ? error : new ApiError('Failed to fetch metrics by domain');
    }
  },

  /**
   * Fetches vitals graph data for a specific metric
   * @param configId - The configuration ID
   * @param metricName - The metric name (FCP, LCP, TTFB, CLS, TBT, SI)
   * @returns Vitals graph response with time-series data
   * @throws ApiError if the request fails
   */
  getVitalsGraph: async (configId: string, metricName: string): Promise<VitalsGraphResponse> => {
    try {
      return await scrapperApi.get<VitalsGraphResponse>('/metrics/vitals-graph', { 
        config_id: configId, 
        metric_name: metricName 
      });
    } catch (error) {
      throw error instanceof ApiError 
        ? error 
        : new ApiError('Failed to fetch vitals graph data');
    }
  },

  /**
   * Starts a scraper configuration
   * @param configId - The configuration ID to start scraping
   * @param deviceType - Optional device type (desktop, mobile, tablet)
   * @returns Success response
   * @throws ApiError if the request fails
   */
  startScrapper: async (configId: string, deviceType?: DeviceType): Promise<{ success: boolean; message?: string }> => {
    try {
      const params: Record<string, string> = {};
      if (deviceType) {
        params.device_type = deviceType;
      }
      return await scrapperApi.post<{ success: boolean; message?: string }>(
        '/scraper/start',
        { config_id: configId },
        params
      );
    } catch (error) {
      throw error instanceof ApiError ? error : new ApiError('Failed to start scraper');
    }
  },

  /**
   * Stops a scraper configuration
   * @param configId - The configuration ID to stop scraping
   * @param deviceType - Optional device type (desktop, mobile, tablet)
   * @returns Success response
   * @throws ApiError if the request fails
   */
  stopScrapper: async (configId: string, deviceType?: DeviceType): Promise<{ success: boolean; message?: string }> => {
    try {
      const params: Record<string, string> = {};
      if (deviceType) {
        params.device_type = deviceType;
      }
      return await scrapperApi.post<{ success: boolean; message?: string }>(
        '/scraper/stop',
        { config_id: configId },
        params
      );
    } catch (error) {
      throw error instanceof ApiError ? error : new ApiError('Failed to stop scraper');
    }
  },

  /**
   * Restarts a scraper configuration
   * @param configId - The configuration ID to restart scraping
   * @returns Success response
   * @throws ApiError if the request fails
   */
  restartScrapper: async (configId: string): Promise<{ success: boolean; message?: string }> => {
    try {
      return await scrapperApi.post<{ success: boolean; message?: string }>('/scraper/restart', { config_id: configId });
    } catch (error) {
      throw error instanceof ApiError ? error : new ApiError('Failed to restart scraper');
    }
  },

  /**
   * Deletes a scraper configuration
   * @param configId - The configuration ID to delete
   * @returns Delete response with config details and note
   * @throws ApiError if the request fails
   */
  deleteConfiguration: async (configId: string): Promise<DeleteConfigurationResponse> => {
    try {
      return await scrapperApi.delete<DeleteConfigurationResponse>(`/scraper/configuration/${configId}`);
    } catch (error) {
      throw error instanceof ApiError ? error : new ApiError('Failed to delete scraper configuration');
    }
  },

  // Placeholder endpoints; user will wire actual paths later.
  listConfigs: async (params?: Record<string, unknown>) => {
    try {
      return await scrapperApi.get<ScrapperListResponse>('/scrapper/configs', params);
    } catch (error) {
      throw error instanceof ApiError ? error : new ApiError('Failed to list scrapper configs');
    }
  },

  createConfig: async (input: CreateScrapperConfigInput) => {
    try {
      return await scrapperApi.post<URLConfig>('/scrapper/configs', input);
    } catch (error) {
      throw error instanceof ApiError ? error : new ApiError('Failed to create scrapper config');
    }
  },

  getConfigById: async (id: string) => {
    try {
      return await scrapperApi.get<URLConfig>(`/scrapper/configs/${id}`);
    } catch (error) {
      throw error instanceof ApiError ? error : new ApiError('Failed to fetch scrapper config');
    }
  },

  updateConfig: async (id: string, input: Partial<CreateScrapperConfigInput>) => {
    try {
      return await scrapperApi.put<URLConfig>(`/scrapper/configs/${id}`, input);
    } catch (error) {
      throw error instanceof ApiError ? error : new ApiError('Failed to update scrapper config');
    }
  },

  deleteConfig: async (id: string) => {
    try {
      return await scrapperApi.delete<{ success: boolean }>(`/scrapper/configs/${id}`);
    } catch (error) {
      throw error instanceof ApiError ? error : new ApiError('Failed to delete scrapper config');
    }
  },

  getMetricsTimeseries: async (params: MetricsTimeseriesParams) => {
    try {
      return await scrapperApi.get<ChartResponse>('/metrics/timeseries', params);
    } catch (error) {
      throw error instanceof ApiError ? error : new ApiError('Failed to fetch metrics timeseries');
    }
  },

  getMetricsDistribution: async (params: MetricsDistributionParams) => {
    try {
      return await scrapperApi.get<ChartResponse>('/metrics/distribution', params);
    } catch (error) {
      throw error instanceof ApiError ? error : new ApiError('Failed to fetch metrics distribution');
    }
  },

  getErrorsTable: async (params: ErrorTableParams) => {
    try {
      return await scrapperApi.get<ErrorTableEntry[]>('/errors/table', params as Record<string, unknown>);
    } catch (error) {
      throw error instanceof ApiError ? error : new ApiError('Failed to fetch errors table');
    }
  },

  getErrorsPie: async (params: { startDate: string; endDate: string }) => {
    try {
      return await scrapperApi.get<ChartResponse>('/errors/pie', params);
    } catch (error) {
      throw error instanceof ApiError ? error : new ApiError('Failed to fetch error types distribution');
    }
  },

  getErrorsSeverity: async (params: { startDate: string; endDate: string }) => {
    try {
      return await scrapperApi.get<ChartResponse>('/errors/severity', params);
    } catch (error) {
      throw error instanceof ApiError ? error : new ApiError('Failed to fetch error severity data');
    }
  },

  getChecksTable: async (params: ElementChecksParams) => {
    try {
      return await scrapperApi.get<ElementCheckEntry[]>('/checks/table', params as Record<string, unknown>);
    } catch (error) {
      throw error instanceof ApiError ? error : new ApiError('Failed to fetch element checks table');
    }
  },

  getElementSuccessRate: async (params: { startDate: string; endDate: string }) => {
    try {
      return await scrapperApi.get<ChartResponse>('/checks/success-rate', params);
    } catch (error) {
      throw error instanceof ApiError ? error : new ApiError('Failed to fetch element success rate data');
    }
  },

  /**
   * Fetches overall stats summary
   * @param params - Date range parameters
   * @returns Stats summary including total URLs, metrics, and averages
   * @throws ApiError if the request fails
   */
  getStatsSummary: async (params: StatsParams) => {
    try {
      return await scrapperApi.get<StatsSummaryResponse>('/stats/summary', params as Record<string, unknown>);
    } catch (error) {
      throw error instanceof ApiError ? error : new ApiError('Failed to fetch stats summary');
    }
  },

  /**
   * Fetches performance metrics summary
   * @param params - Date range parameters
   * @returns Performance summary including FCP, LCP, TTFB with targets
   * @throws ApiError if the request fails
   */
  getPerformanceSummary: async (params: StatsParams) => {
    try {
      return await scrapperApi.get<PerformanceSummaryResponse>('/stats/performance-summary', params as Record<string, unknown>);
    } catch (error) {
      throw error instanceof ApiError ? error : new ApiError('Failed to fetch performance summary');
    }
  },

  /**
   * Fetches functional checks data
   * @param params - Query parameters including limit, url, config_id
   * @returns Functional checks response with total and checks array
   * @throws ApiError if the request fails
   */
  getFunctionalChecks: async (params?: FunctionalChecksParams): Promise<FunctionalChecksResponse> => {
    try {
      return await scrapperApi.get<FunctionalChecksResponse>('/metrics/functional-checks', params as Record<string, unknown>);
    } catch (error) {
      throw error instanceof ApiError ? error : new ApiError('Failed to fetch functional checks');
    }
  },

  /**
   * Fetches errors data
   * @param params - Query parameters including limit, url, config_id, start_date, end_date
   * @returns Errors response with total and errors array
   * @throws ApiError if the request fails
   */
  getErrors: async (params?: ErrorsParams): Promise<ErrorsResponse> => {
    try {
      return await scrapperApi.get<ErrorsResponse>('/metrics/errors', params as Record<string, unknown>);
    } catch (error) {
      throw error instanceof ApiError ? error : new ApiError('Failed to fetch errors');
    }
  },

  /**
   * Fetches top predicted winner products for a configuration
   * @param configId - The configuration ID
   * @returns Winners response with products array and metadata
   * @throws ApiError if the request fails
   */
  getWinners: async (configId: string): Promise<WinnersResponse> => {
    try {
      return await scrapperApi.get<WinnersResponse>(`/insights/config/${configId}/winners`);
    } catch (error) {
      throw error instanceof ApiError ? error : new ApiError('Failed to fetch winners');
    }
  },

  /**
   * Fetches trending products for a configuration
   * @param configId - The configuration ID
   * @returns Trending response with products array and metadata
   * @throws ApiError if the request fails
   */
  getTrending: async (configId: string): Promise<TrendingResponse> => {
    try {
      return await scrapperApi.get<TrendingResponse>(`/insights/config/${configId}/trending`);
    } catch (error) {
      throw error instanceof ApiError ? error : new ApiError('Failed to fetch trending products');
    }
  },

  /**
   * Fetches products losing visibility for a configuration
   * @param configId - The configuration ID
   * @returns Losing response with products array and metadata
   * @throws ApiError if the request fails
   */
  getLosing: async (configId: string): Promise<LosingResponse> => {
    try {
      return await scrapperApi.get<LosingResponse>(`/insights/config/${configId}/losing`);
    } catch (error) {
      throw error instanceof ApiError ? error : new ApiError('Failed to fetch losing products');
    }
  },

  /**
   * Get element templates
   * @param None
   * @returns ElementTemplatesResponse with array of templates
   * @throws ApiError if the request fails
   */
  getElementTemplates: async (): Promise<ElementTemplatesResponse> => {
    try {
      return await scrapperApi.get<ElementTemplatesResponse>('/scraper/element-templates');
    } catch (error) {
      throw error instanceof ApiError ? error : new ApiError('Failed to fetch element templates');
    }
  },

  /**
   * Get standard thresholds
   * @returns StandardThresholdsResponse with standard threshold values
   * @throws ApiError if the request fails
   */
  getStandardThresholds: async (): Promise<StandardThresholdsResponse> => {
    try {
      return await scrapperApi.get<StandardThresholdsResponse>('/scraper/standard-thresholds');
    } catch (error) {
      throw error instanceof ApiError ? error : new ApiError('Failed to fetch standard thresholds');
    }
  },
};

export default scrapperService;


